import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Users, Search, BadgeCheck, Power } from 'lucide-react';
import { api } from '../api';
import { PageLoading, Modal, useToast, Confirm } from '../components/ui';

const empty = { first_name: '', last_name: '', phone: '', email: '', notes: '', qualificationIds: [], active: true };

export default function Personnel() {
  const toast = useToast();
  const [people, setPeople] = useState(null);
  const [quals, setQuals] = useState([]);
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const load = () => api.get('/personnel').then((d) => setPeople(d.personnel));
  useEffect(() => { load(); api.get('/qualifications').then((d) => setQuals(d.qualifications)); }, []);
  if (!people) return <PageLoading />;

  const filtered = people.filter((p) =>
    (showInactive || p.active) &&
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(q.toLowerCase())
  );

  const toggleQual = (id) => {
    const has = edit.qualificationIds.includes(id);
    setEdit({ ...edit, qualificationIds: has ? edit.qualificationIds.filter((x) => x !== id) : [...edit.qualificationIds, id] });
  };
  const openEdit = (p) => setEdit(p ? {
    id: p.id, first_name: p.first_name, last_name: p.last_name, phone: p.phone || '', email: p.email || '',
    notes: p.notes || '', active: p.active, qualificationIds: p.qualifications.map((x) => x.id),
  } : { ...empty });

  const save = async () => {
    if (!edit.first_name || !edit.last_name) return toast('Vor- und Nachname erforderlich', 'err');
    try {
      if (edit.id) await api.put(`/personnel/${edit.id}`, edit);
      else await api.post('/personnel', edit);
      setEdit(null); load(); toast('Gespeichert');
    } catch (e) { toast(e.message, 'err'); }
  };
  const remove = async (id) => { try { await api.del(`/personnel/${id}`); load(); toast('Gelöscht'); } catch (e) { toast(e.message, 'err'); } };
  const toggleActive = async (p) => { try { await api.put(`/personnel/${p.id}`, { active: !p.active }); load(); } catch (e) { toast(e.message, 'err'); } };

  return (
    <>
      <div className="page-head">
        <div><h1>Dienstleistende</h1><div className="sub">{people.filter((p) => p.active).length} aktive Personen · Qualifikationen & Vorbelastung</div></div>
        <div className="toolbar"><button className="btn btn-primary" onClick={() => openEdit(null)}><Plus size={16} /> Neue Person</button></div>
      </div>

      <div className="flex flex-wrap" style={{ marginBottom: 14, gap: 12 }}>
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 360 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 13, color: 'var(--text-secondary)' }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…" style={{ paddingLeft: 36 }} />
        </div>
        <label className="checkbox-row"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Inaktive anzeigen</label>
      </div>

      <div className="card">
        {filtered.length === 0 ? <div className="empty"><Users size={40} /><p>Keine Personen.</p></div> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Name</th><th>Qualifikationen</th><th>Vorbelastung</th><th>Kontakt</th><th></th></tr></thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} style={{ opacity: p.active ? 1 : 0.5 }}>
                    <td><strong>{p.last_name}, {p.first_name}</strong>{!p.active && <span className="badge badge-gray" style={{ marginLeft: 8 }}>inaktiv</span>}</td>
                    <td><div className="tag-list">{p.qualifications.length ? p.qualifications.map((qu) => <span key={qu.id} className="qual-tag" title={qu.name}>{qu.code}</span>) : <span className="muted small">—</span>}</div></td>
                    <td>
                      <span className="badge badge-gray" title="Absolvierte Wachen / Stunden">{p.load.count} W · {p.load.hours} h</span>
                      {p.passedOver > 0 && <span className="badge badge-amber" title="Beworben, aber nicht eingeteilt (abgeschlossene Wachen)" style={{ marginLeft: 4 }}>{p.passedOver}× übergangen</span>}
                    </td>
                    <td className="small muted">{p.phone || p.email || '—'}</td>
                    <td className="row-actions">
                      <button className="btn btn-ghost btn-sm" title={p.active ? 'Deaktivieren' : 'Aktivieren'} onClick={() => toggleActive(p)}><Power size={14} /></button>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(p)}><Pencil size={14} /></button>
                      <button className="btn btn-danger btn-sm" onClick={() => setDel(p)}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {edit && (
        <Modal title={edit.id ? 'Person bearbeiten' : 'Neue Person'} onClose={() => setEdit(null)}
          footer={<><button className="btn btn-secondary" onClick={() => setEdit(null)}>Abbrechen</button><button className="btn btn-primary" onClick={save}>Speichern</button></>}>
          <div className="form-row">
            <div className="form-group"><label>Vorname</label><input value={edit.first_name} onChange={(e) => setEdit({ ...edit, first_name: e.target.value })} /></div>
            <div className="form-group"><label>Nachname</label><input value={edit.last_name} onChange={(e) => setEdit({ ...edit, last_name: e.target.value })} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Telefon</label><input value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} /></div>
            <div className="form-group"><label>E-Mail</label><input type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} /></div>
          </div>
          <div className="form-group">
            <label><BadgeCheck size={13} style={{ verticalAlign: -2 }} /> Qualifikationen</label>
            <div className="tag-list" style={{ gap: 8 }}>
              {quals.map((qu) => {
                const on = edit.qualificationIds.includes(qu.id);
                return (
                  <button type="button" key={qu.id} onClick={() => toggleQual(qu.id)}
                    className="qual-tag" title={qu.name}
                    style={{ cursor: 'pointer', border: on ? '1px solid var(--primary-color)' : '1px solid var(--border-color)', background: on ? 'var(--primary-light)' : '#fff', color: on ? 'var(--primary-dark)' : 'var(--text-secondary)', padding: '6px 12px' }}>
                    {qu.code} – {qu.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="form-group"><label>Notizen</label><textarea value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></div>
          {edit.id && <label className="checkbox-row"><input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} /> aktiv</label>}
        </Modal>
      )}
      {del && <Confirm text={`Person „${del.first_name} ${del.last_name}" wirklich löschen?`} onConfirm={() => remove(del.id)} onClose={() => setDel(null)} />}
    </>
  );
}
